import React, { useEffect } from 'react';

interface FAQ {
  question: string;
  answer: string;
}

interface MetaProps {
  title?: string;
  description?: string;
  keywords?: string;
  category?: string;
  faqs?: FAQ[];
  is404?: boolean;
}

const defaultPageTitle = 'Tools Copilot - 140+ Free Online Web Tools & Utilities';
const defaultDescription = 'Access 140+ free online tools for PDF editing, image conversion & calculators. 100% private, client-side in-browser processing with zero server uploads.';
const defaultKeywords = "free online tools, pdf tools, compress pdf, image editor, video downloader, online calculators, unit converters, developer tools, text utilities, secure client-side tools";

export const Meta: React.FC<MetaProps> = ({ title, description, keywords, category, faqs, is404 }) => {
  useEffect(() => {
    // 1. Format Title strictly between 50-60 characters
    let finalTitle = defaultPageTitle;
    if (title) {
      if (title.includes('ToolsCopilot') || title.includes('Tools Copilot')) {
        finalTitle = title;
      } else {
        finalTitle = `${title} (Free & Online) — ToolsCopilot`;
        if (finalTitle.length < 50) finalTitle = `Free ${title} Online (100% Private) — ToolsCopilot`;
        if (finalTitle.length < 50) finalTitle = `Free ${title} Online Web Tool (100% Private) — ToolsCopilot`;
        if (finalTitle.length > 60) finalTitle = `${title} Free Online — ToolsCopilot`;
        if (finalTitle.length > 60) finalTitle = `${title} — ToolsCopilot`;
        if (finalTitle.length > 60) finalTitle = finalTitle.slice(0, 57) + '...';
      }
    }

    // 2. Format Meta Description strictly between 120-158 characters
    let finalDescription = defaultDescription;
    if (description) {
      const cleanDesc = description.replace(/\\'/g, "'").replace(/"/g, '&quot;').replace(/\\+$/, '').trim();
      finalDescription = `${cleanDesc} 100% free, private & fast in-browser tool on ToolsCopilot with zero server uploads.`;
      if (finalDescription.length > 158) {
        finalDescription = `${cleanDesc} 100% free & private in your browser with ToolsCopilot.`;
      }
      if (finalDescription.length > 158) {
        finalDescription = cleanDesc.length <= 155 ? cleanDesc : cleanDesc.slice(0, 155) + '...';
      }
      if (finalDescription.length < 120 && title) {
        finalDescription = `Use our free online ${title}. ${cleanDesc} 100% private and secure client-side in-browser utility.`;
        if (finalDescription.length > 158) {
          finalDescription = finalDescription.slice(0, 155) + '...';
        }
      }
      finalDescription = finalDescription.replace(/\\+$/, '').trim();
    }

    const finalKeywords = keywords || defaultKeywords;

    // 3. Compute Normalized, Direct Canonical URL (no trailing slash except root, no query params, HTTPS non-www)
    const rawPath = typeof window !== 'undefined' ? window.location.pathname : '/';
    let cleanPath = rawPath.replace(/\/+$/, '');
    if (!cleanPath) cleanPath = '/';
    
    // Canonical aliasing fixes if landed via old alias
    if (cleanPath === '/tool/time-duration-calculator') cleanPath = '/tool/time-converter';
    if (cleanPath === '/tool/color-palette-tool') cleanPath = '/tool/color-palette';
    if (cleanPath === '/tool/text-to-word') cleanPath = '/tool/doc-to-pdf';
    if (cleanPath.startsWith('/category')) cleanPath = '/';
    if (cleanPath === '/tool/image-enhancer') cleanPath = '/tool/image-upscaler';
    if (cleanPath === '/tool/about-us') cleanPath = '/about-us';
    if (cleanPath === '/tool/contact-us') cleanPath = '/contact-us';
    if (cleanPath === '/tool/privacy-policy') cleanPath = '/privacy-policy';
    if (cleanPath === '/tool/terms-of-service') cleanPath = '/terms-of-service';
    if (cleanPath === '/tool/sitemap') cleanPath = '/sitemap';
    if (cleanPath === '/tool/blog') cleanPath = '/blog';

    const canonicalUrl = cleanPath === '/' ? 'https://toolscopilot.com/' : `https://toolscopilot.com${cleanPath}`;

    // 4. Update Document Title
    document.title = finalTitle;

    const setMetaTag = (nameOrProperty: 'name' | 'property', attrValue: string, content: string) => {
      let tag = document.querySelector(`meta[${nameOrProperty}="${attrValue}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(nameOrProperty, attrValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    // 5. Standard SEO Meta Tags
    setMetaTag('name', 'description', finalDescription);
    setMetaTag('name', 'keywords', finalKeywords);
    setMetaTag('name', 'robots', is404 ? 'noindex, nofollow' : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1');

    // 6. Open Graph Tags
    setMetaTag('property', 'og:title', finalTitle);
    setMetaTag('property', 'og:description', finalDescription);
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:type', 'website');
    setMetaTag('property', 'og:site_name', 'Tools Copilot');
    setMetaTag('property', 'og:image', 'https://i.ibb.co/3kZ8j2c/og-image.png');

    // 7. Twitter Card Tags
    setMetaTag('property', 'twitter:title', finalTitle);
    setMetaTag('property', 'twitter:description', finalDescription);
    setMetaTag('property', 'twitter:url', canonicalUrl);
    setMetaTag('property', 'twitter:card', 'summary_large_image');
    setMetaTag('property', 'twitter:image', 'https://i.ibb.co/3kZ8j2c/og-image.png');

    // 8. Canonical & Hreflang Tags
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', canonicalUrl);

    let hreflangDefault = document.querySelector('link[rel="alternate"][hreflang="x-default"]');
    if (hreflangDefault) {
      hreflangDefault.setAttribute('href', canonicalUrl);
    }
    let hreflangEn = document.querySelector('link[rel="alternate"][hreflang="en"]');
    if (hreflangEn) {
      hreflangEn.setAttribute('href', canonicalUrl);
    }

    // 9. Dynamic JSON-LD Structured Data Injection
    let schemaScript = document.getElementById('dynamic-tool-schema') as HTMLScriptElement;
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = 'dynamic-tool-schema';
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }

    const schemaGraph: any[] = [];

    if (title) {
      // SoftwareApplication / WebApplication Schema
      schemaGraph.push({
        "@type": "WebApplication",
        "@id": `${canonicalUrl}#software`,
        "name": `${title} - Tools Copilot`,
        "url": canonicalUrl,
        "description": finalDescription,
        "applicationCategory": category || "UtilitiesApplication",
        "operatingSystem": "All",
        "browserRequirements": "Requires JavaScript. Requires HTML5.",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "provider": {
          "@type": "Organization",
          "name": "Tools Copilot",
          "url": "https://toolscopilot.com"
        }
      });

      // BreadcrumbList Schema
      schemaGraph.push({
        "@type": "BreadcrumbList",
        "@id": `${canonicalUrl}#breadcrumb`,
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": "https://toolscopilot.com/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": category || "Tools",
            "item": `https://toolscopilot.com/#${(category || 'tools').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": title,
            "item": canonicalUrl
          }
        ]
      });

      // FAQPage Schema
      if (faqs && faqs.length > 0) {
        schemaGraph.push({
          "@type": "FAQPage",
          "@id": `${canonicalUrl}#faq`,
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": f.answer
            }
          }))
        });
      }
    }

    if (schemaGraph.length > 0) {
      schemaScript.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@graph": schemaGraph
      });
    } else {
      schemaScript.textContent = '';
    }

  }, [title, description, keywords, category, faqs]);

  return null;
};
